import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material";
import { useAgruparArea, useAreas, useBorrarArea, useCrearArea } from "../../queries/useTemas";
import { useSuperAreas } from "../../queries/useDocentes";
import { mensajeDeError } from "../../utils/errores";

/**
 * Administrar el catálogo de áreas de investigación (coordinador): agregar,
 * borrar y **meter cada una en una supercategoría**.
 *
 * La supercategoría es la agrupación con la que se filtra la vista de Docentes;
 * es solo de vitrina, así que cambiarla no afecta al armado de comisiones y por
 * eso basta un selector por fila, sin confirmación. Crear o renombrar
 * supercategorías se hace desde el admin de Django: son diez y casi no cambian.
 *
 * Las áreas se comparten entre temas y profesores; borrar un área la quita de todos.
 */
export default function AdministrarAreasDialog({ onClose }: { onClose: () => void }) {
  const { data: areas = [], isLoading } = useAreas();
  const { data: superareas = [] } = useSuperAreas();
  const crear = useCrearArea();
  const borrar = useBorrarArea();
  const agrupar = useAgruparArea();
  const [nombre, setNombre] = useState("");
  const [nuevoId, setNuevoId] = useState<number | null>(null);

  const agregar = () => {
    const n = nombre.trim();
    if (n) crear.mutate(n, { onSuccess: (area) => (setNombre(""), setNuevoId(area.id)) });
  };

  // Al crear un área, la fila aparece tras el refetch (y la lista puede re-renderizarse).
  // Se re-scrollea sobre ella durante un momento para asegurar el foco pese a los re-renders;
  // el resaltado se apaga después.
  useEffect(() => {
    if (nuevoId == null) return;
    let n = 0;
    let tickId = 0;
    const tick = () => {
      document.querySelector(`[data-area-id="${nuevoId}"]`)?.scrollIntoView({ block: "center" });
      if (n++ < 12) tickId = window.setTimeout(tick, 100);
    };
    tick();
    const limpiar = window.setTimeout(() => setNuevoId(null), 3000);
    return () => {
      clearTimeout(tickId);
      clearTimeout(limpiar);
    };
  }, [nuevoId]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Administrar áreas de investigación</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Las áreas se comparten entre temas y profesores; borrar un área la quita de todos los que la tenían. La supercategoría
          es solo para agrupar la vista de Docentes: cambiarla no afecta al armado de comisiones.
        </Typography>

        {(crear.isError || borrar.isError || agrupar.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mensajeDeError(crear.error || borrar.error || agrupar.error, "No se pudo completar la operación.")}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Nueva área"
            placeholder="Nombre del área"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && agregar()}
          />
          <Button variant="contained" startIcon={<AddIcon />} disabled={crear.isPending || !nombre.trim()} onClick={agregar}>
            Agregar
          </Button>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : areas.length ? (
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 380, overflowY: "auto" }}>
            {areas.map((a, i) => (
              <Stack
                key={a.id}
                data-area-id={a.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: i === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                  bgcolor: a.id === nuevoId ? "action.selected" : "transparent",
                  transition: "background-color 0.4s",
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                  {a.nombre}
                </Typography>
                <TextField
                  select
                  size="small"
                  variant="standard"
                  label={undefined}
                  value={a.supercategoria ?? ""}
                  onChange={(e) => agrupar.mutate({ id: a.id, supercategoria: e.target.value ? Number(e.target.value) : null })}
                  disabled={agrupar.isPending}
                  sx={{ width: 210, flexShrink: 0 }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <Typography variant="body2" color="text.disabled">
                      Sin agrupar
                    </Typography>
                  </MenuItem>
                  {superareas.map((sa) => (
                    <MenuItem key={sa.id} value={sa.id}>
                      {sa.nombre}
                    </MenuItem>
                  ))}
                </TextField>
                <IconButton size="small" color="error" disabled={borrar.isPending} onClick={() => borrar.mutate(a.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
            Todavía no hay áreas.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
